/**
 * Project Grid block entry.
 *
 * Importing the SCSS here makes Vite bundle the block's CSS alongside its JS,
 * so the theme ships both together.
 *
 * Two behaviours live here:
 *
 * 1. The entrance: the section sits below the fold, so a plain CSS animation
 *    would have played out before anyone scrolled to it. The `data-reveal`
 *    attribute is set here and only here — with the script blocked the
 *    attribute never appears and the mosaic renders in full.
 *
 *    The heading and the button take the section's own trigger. The cards take
 *    one each: the mosaic is taller than the window, so a single trigger would
 *    spend the whole cascade on the rows nobody has scrolled to yet.
 *
 * 2. The filters, on the pages that carry them. Every project is already in the
 *    document, so narrowing the mosaic is a matter of hiding cards rather than
 *    fetching anything: no request, no spinner, no history entry. The bar is
 *    rendered hidden by PHP and unhidden here, so a blocked script leaves the
 *    full mosaic and no dead buttons.
 *
 * Everything else is links and CSS, including the hover, so the reduced-motion
 * guard in main.scss covers the lot without a second check here.
 */

import './index.scss';

/**
 * Play the settle animation again on a card that has already played it.
 *
 * An animation that has finished does not start over because an attribute was
 * written a second time, and taking it off and putting it back in the same task
 * is a change the browser never sees. Reading a layout property in between is
 * what forces it to.
 *
 * @param {HTMLElement} item The `.project-grid__item` to restart.
 * @returns {void}
 */
function restart(item) {
    item.removeAttribute('data-settle');
    void item.offsetWidth;
    item.setAttribute('data-settle', '');
}

/**
 * Wire one section's filter bar, if it has one.
 *
 * @param {HTMLElement} section The `.project-grid` element.
 * @returns {void}
 */
function setupFilters(section) {
    const bar = section.querySelector('.project-grid__filters');
    const list = section.querySelector('.project-grid__items');

    if (!bar || !list) {
        return;
    }

    const buttons = [...bar.querySelectorAll('.project-grid__filter')];
    const items = [...list.querySelectorAll('.project-grid__item')];
    const status = bar.querySelector('.project-grid__status');

    if (!buttons.length || !items.length) {
        return;
    }

    /**
     * Show the cards matching one filter and restate the mosaic's rhythm.
     *
     * @param {string} value Category slug, or an empty string for all of them.
     * @returns {void}
     */
    const apply = (value, settle = false) => {
        let shown = 0;

        for (const item of items) {
            const matches = '' === value || item.dataset.category === value;

            item.hidden = !matches;

            /* The wide cards are the first and fifth of every six — counted
             * among the cards still on screen, which is the whole reason this
             * runs in JavaScript rather than in `nth-child`. */
            item.toggleAttribute('data-wide', matches && (0 === shown % 6 || 4 === shown % 6));

            if (matches) {
                shown += 1;

                if (settle) {
                    /* A card the entrance never reached — filtered away while
                     * below the fold, so it has no size and nothing observing it
                     * ever fired — would come back invisible. Being chosen by a
                     * filter counts as having arrived. Only on a real press:
                     * the first call runs before anyone has scrolled, and doing
                     * it there would spend the whole entrance at page load. */
                    item.dataset.reveal = 'in';
                    restart(item);
                }
            }
        }

        let pressedLabel = '';

        for (const button of buttons) {
            const pressed = (button.dataset.filter ?? '') === value;

            button.setAttribute('aria-pressed', String(pressed));

            if (pressed) {
                pressedLabel = button.textContent.trim();
            }
        }

        if (status && status.dataset.template) {
            status.textContent = status.dataset.template.replace('%s', pressedLabel);
        }
    };

    bar.addEventListener('click', (event) => {
        const button = event.target.closest('.project-grid__filter');

        if (button && bar.contains(button)) {
            apply(button.dataset.filter ?? '', true);
        }
    });

    /* Hand the rhythm over before unhiding the bar, so the mosaic never paints
     * once under `nth-child` and again under `data-wide`. */
    list.dataset.filtering = '';
    apply('');
    bar.hidden = false;
}

const sections = document.querySelectorAll('.project-grid');

for (const section of sections) {
    setupFilters(section);
}

if (sections.length && 'IntersectionObserver' in window) {
    const observer = new IntersectionObserver(
        (entries) => {
            for (const entry of entries) {
                if (entry.isIntersecting) {
                    entry.target.setAttribute('data-reveal', 'in');
                    observer.unobserve(entry.target);
                }
            }
        },
        { rootMargin: '0px 0px -15% 0px' }
    );

    /* The cards, one trigger each. A row crosses the line in the same callback,
     * so the delay is shared out within that batch and the row reads left to
     * right; the next row starts from nothing again rather than inheriting a
     * delay that grows down a mosaic of any length. */
    const cards = new IntersectionObserver(
        (entries) => {
            let entering = 0;

            for (const entry of entries) {
                if (!entry.isIntersecting) {
                    continue;
                }

                entry.target.style.setProperty('--project-grid-enter-delay', `${entering * 70}ms`);
                entry.target.setAttribute('data-reveal', 'in');
                cards.unobserve(entry.target);
                entering += 1;
            }
        },
        { rootMargin: '0px 0px -10% 0px' }
    );

    for (const section of sections) {
        section.setAttribute('data-reveal', 'pending');
        observer.observe(section);

        for (const item of section.querySelectorAll('.project-grid__item')) {
            cards.observe(item);
        }
    }
}
